import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import TableCSVExport from '../../../component/TableCSVExport';

// Helper.
import Helper from '../../../helpers/Helper';

// Types.
import Wait from "../../../types/instance/Wait";
import BatchesDataTable from '../../../types/instance/tables/BatchesDataTable';

// Components.
import BatchBreakdown from '../BatchBreakdown';
import GenericBreakdown from '../../GenericBreakdown';
import ReactTable from '../../ReactTable';

// Constants.
import { DATA_LOADING, INSTANCE_TYPE_SQLSERVER } from "../../Constants";
import { InstanceContext } from "../../../context/InstanceContext";
import StatementToolTip from "./StatementToolTip";
import { getDefaultPageSize, getToolTipPosition } from "../../../helpers/utils";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import SearchInput from '../../SearchInput';

function BatchesTable(props: {
    loading: number,
    filtersParameters: string,
    instanceId: number,
    instanceTime: number,
    waits: Wait[],
    batchesDataTable: BatchesDataTable[],
    isBatchesForStatement: boolean, currentTab?: string,
    onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void
}) {
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });
    const { instances } = useContext(InstanceContext);
    const instance = instances.find(i => i.id === props.instanceId)
    const instanceType = instance?.type ?? ''
    const timeRangeContext = useContext(TimeRangeContext)

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        // Clear Filter if tab is changed
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    const data = useMemo(() => props.batchesDataTable.filter(row => {
        return (
            (row.batchsqlhash === null ? '' : row.batchsqlhash).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.sqltext === null ? '' : row.sqltext).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.batch.sqlid === null ? '' : row.batch.sqlid).toLowerCase().includes(tableFilter.toLowerCase())
        )
    }), [props.batchesDataTable, tableFilter]);

    // Calculate total wait time of all batches for Weight % calculation
    // Use props.batchesDataTable (not filtered data) to ensure percentages are relative to all batches
    const totalBatchesWaitTime = useMemo(() => {
        return props.batchesDataTable.reduce((sum, row) => sum + (row.waittime || 0), 0);
    }, [props.batchesDataTable]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'batch',
            disableSortBy: true
        },
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'color',
            disableSortBy: true
        },
        {
            accessor: 'batchwaittime',
            disableSortBy: true
        },
        {
            Header: '#',
            accessor: 'batchsqlhash',
            className: 'concatenate-small col-width-10',
            defaultCanSort: true,
            sortType: 'alphanumeric',
            Cell: ({ cell }) => (
                <React.Fragment>
                    <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                    <Link
                        to={`/instances/${props.instanceId}/activity/batch/${cell.row.values.batchsqlhash}/text?${timeRangeContext.getTimeRangeRedirectString()}`}>
                        {props.onStatementClick && <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page" />} {((cell.row.values.batchsqlhash === null) ? '-' : cell.row.values.batchsqlhash)}
                    </Link>
                </React.Fragment>
            ),
        },
        {
            Header: 'Batch Statement',
            accessor: 'sqltext',
            className: 'concatenate',
            disableSortBy: true,
            Cell: ({ cell }) => {
                if (!cell.row.values.sqltext) return <React.Fragment>-</React.Fragment>;

                const link = `/instances/${props.instanceId}/activity/batch/${cell.row.values.batchsqlhash}/text?${timeRangeContext.getTimeRangeRedirectString()}`;

                // Has callback = normal view with panel: show icon + click opens panel
                if (props.onStatementClick) {
                    return (
                        <span
                            style={{ cursor: 'pointer', color: '#0d6efd' }}
                            onClick={(e) => {
                                e.preventDefault();
                                props.onStatementClick!(cell.row.values.batchsqlhash, 'batch');
                            }}
                        >
                            <i className="fal fa-sidebar-flip fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in side panel" />
                            {cell.row.values.sqltext.substring(0, 200)}
                        </span>
                    );
                }

                // No callback = inside panel or no panel support: just link, no icon
                return <Link to={link}>{cell.row.values.sqltext.substring(0, 200)}</Link>;
            }
        },
        {
            Header: 'Breakdown',
            accessor: 'statement-waits',
            className: 'statement-waits col-width-10',
            disableSortBy: true,
            Cell: ({ cell }) => (
                cell.row.values.time !== null ? (
                    cell.row.values.id < 10 ? (
                        <BatchBreakdown filterParameters={props.filtersParameters} batch={cell.row.values.batch}
                            waits={props.waits} instanceId={props.instanceId}
                            instanceTime={props.instanceTime}
                            maxWaitTime={data[0]?.waittime} />
                    ) : (
                        cell.row.values.id < 20 ? (
                            <GenericBreakdown individualTime={cell.row.values.waittime} totalTime={data[0]?.waittime || props.instanceTime}
                                color="#999" tooltip="" />
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    )
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Executions',
            accessor: 'executions',
            headerClassName: 'text-end col-width-5',
            className: 'text-end',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                Helper.getFormattedNumber(cell.row.values.executions)
            )
        },
        {
            Header: 'Average Time',
            accessor: 'averagetime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                cell.row.values.averagetime !== null && cell.row.values.executions !== null && cell.row.values.executions > 0 ? (
                    <React.Fragment>
                        {Helper.getTimeInEnglish(cell.row.values.averagetime)}
                    </React.Fragment>
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Total Time',
            accessor: 'waittime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5 text-nowrap',
            defaultCanSort: true,
            sortType: (rowA, rowB, columnId) => {
                const aWait = Number(rowA.values[columnId]) || 0;
                const bWait = Number(rowB.values[columnId]) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(rowA.values['batchsqlhash']) || 0;
                const bHash = Number(rowB.values['batchsqlhash']) || 0;
                return aHash - bHash;
            },
            Cell: ({ cell }) => (
                cell.row.values.waittime !== null && cell.row.values.waittime > 0 ? (
                    <React.Fragment>
                        {Helper.getTimeInEnglish(cell.row.values.waittime)}
                    </React.Fragment>
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Weight %',
            accessor: 'weight',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {
                if (cell.row.values.waittime === null) {
                    return <React.Fragment>-</React.Fragment>;
                }

                // For batches called by a statement, calculate as percentage of total batches wait time
                // Otherwise, calculate as percentage of instance time
                const baseTime = Boolean(props.isBatchesForStatement) === true
                    ? totalBatchesWaitTime
                    : props.instanceTime;

                const percentage = baseTime > 0 ? (100 / baseTime) * cell.row.values.waittime : 0;
                const display = percentage.toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                return <React.Fragment>{display}%</React.Fragment>;
            }
        },
    ], [props.instanceId, props.instanceTime, props.isBatchesForStatement, props.filtersParameters, props.waits, instanceType, totalBatchesWaitTime, data]);

    let hiddenColumns: string[] = [];

    switch (instanceType) {
        case INSTANCE_TYPE_SQLSERVER:
            hiddenColumns = ['batch', 'id', 'color', 'batchwaittime', 'averagetime', 'executions'];
            break;

        default:
            hiddenColumns = ['batch', 'id', 'color', 'batchwaittime'];

            break;
    }

    if (Boolean(props.isBatchesForStatement)) {
        hiddenColumns = ['batch', 'id', 'color', 'batchwaittime', 'averagetime', 'executions', 'statement-waits']
    }

    const tableInstance = useTable({
        columns,
        data,
        initialState: {
            hiddenColumns: hiddenColumns,
            pageIndex: initialPageIndex,
            pageSize: initialPageSize,
        }
    },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    // Calculate base time for Weight % calculation
    const weightBaseTime = useMemo(() => {
        return Boolean(props.isBatchesForStatement) === true
            ? totalBatchesWaitTime
            : props.instanceTime;
    }, [props.instanceTime, props.isBatchesForStatement, totalBatchesWaitTime]);

    return (
        <React.Fragment>
            <div className="row row-cols-1 row-cols-md-2 table-search">
                <div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Batches"
                        excludeColumns={['statement-waits']}
                        instanceTime={weightBaseTime}
                    />
                </div>
                <div className="col col-md-2">
                    <SearchInput
                        value={tableFilter}
                        onChange={(e) => setTableFilter(e.target.value)}
                        onClear={clearTableFilter}
                        autoFocus
                    />
                </div>
            </div>
            <ReactTable
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                prepareRow={prepareRow}
                page={page}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageCount={pageCount}
                gotoPage={gotoPage}
                nextPage={nextPage}
                previousPage={previousPage}
                setPageSize={setPageSize}
                pageIndex={pageIndex}
                pageSize={pageSize}
                dataLength={data.length}
                error={null}
                loading={props.loading === DATA_LOADING}
                renderCell={(cell) => {
                    if (cell.column.id === "sqltext") {
                        return (
                            <div className='tooltip-scroll ellipsis'
                                onMouseEnter={(event) => setVisibleToolTip({
                                    id: cell.row.values.batchsqlhash,
                                    position: getToolTipPosition(event)
                                })}
                                onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                            >
                                {cell.render('Cell')}
                                {cell.row.values.batchsqlhash === visibleToolTip.id && (
                                    <StatementToolTip instanceType={instanceType}
                                        position={visibleToolTip.position}
                                        id={cell.row.values.batchsqlhash}
                                        onClick={() => props.onStatementClick?.(cell.row.values.batchsqlhash, 'batch')}
                                        cell={cell} />
                                )}
                            </div>
                        );
                    }
                    return cell.render('Cell');
                }}
            />
        </React.Fragment>
    );
}

export default BatchesTable;
